import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import type { ServiceTrack } from '../types';

const trackClass = (track: ServiceTrack): string => {
  if (track === 'software') return 'service-track-badge service-track-badge--software';
  if (track === 'hardware') return 'service-track-badge service-track-badge--hardware';
  return 'service-track-badge service-track-badge--both';
};

const OfferPage: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const { language } = useLanguage();
  const t = translations[language];

  const offer = t.offerPages.find((o) => o.slug === offerId);
  if (!offer) return <Navigate to={language === 'fr' ? '/' : `/${language}`} replace />;

  const relevantServices = t.services.items.filter((s) => s.offerPage === offer.id);
  const showAiBlock = offer.id === 'poc-mvp';
  const phaseCount = offer.phases.length;
  const showPhaseIndex = phaseCount > 1;

  const trackLabel = (track: ServiceTrack): string => {
    if (track === 'software') return t.services.trackSoftware;
    if (track === 'hardware') return t.services.trackHardware;
    return t.services.trackBoth;
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      const offset = 58;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <section
        className="offer-page-hero"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/images/bg.jpg)`,
        }}
      >
        <div className="hero-overlay" aria-hidden />
        <div className="container">
          <h1 className="offer-page-title">{offer.title}</h1>
          <p className="offer-page-subtitle">{offer.subtitle}</p>
          <button type="button" className="hero-cta" onClick={scrollToContact}>
            {offer.ctaLabel}
          </button>
        </div>
      </section>

      <section className="offer-page-body">
        <div className="container">
          <p className="offer-page-intro">{offer.pageIntro}</p>

          {phaseCount > 0 && (
            <div
              className="offers-grid"
              style={{ '--offers-grid-columns': phaseCount } as React.CSSProperties}
            >
              {offer.phases.map((phase, i) => (
                <article key={i} className="bundle-card">
                  {showPhaseIndex && (
                    <span className="bundle-index" aria-hidden="true">
                      {i + 1}
                    </span>
                  )}
                  <h3 className="bundle-card-title">{phase.title}</h3>
                  <p className="bundle-card-subtitle">{phase.subtitle}</p>
                  <p className="bundle-card-text">{phase.description}</p>
                </article>
              ))}
            </div>
          )}

          {showAiBlock && (
            <div className="mission-ai-block">
              <h3 className="mission-ai-title">{t.mission.aiTitle}</h3>
              <div className="mission-ai-columns">
                <div className="mission-ai-column mission-ai-column--helps">
                  <p className="mission-ai-column-label">{t.mission.aiColumnHelpsLabel}</p>
                  <ul className="mission-ai-list">
                    {t.mission.aiHelps.map((line, idx) => (
                      <li key={`ai-help-${idx}`}>{line}</li>
                    ))}
                  </ul>
                </div>
                <div className="mission-ai-column">
                  <p className="mission-ai-column-label">{t.mission.aiColumnLimitsLabel}</p>
                  <ul className="mission-ai-list">
                    {t.mission.aiLimits.map((line, idx) => (
                      <li key={`ai-limit-${idx}`}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {relevantServices.length > 0 && (
            <div className="offer-page-services">
              <h2 className="section-title">{t.services.title}</h2>
              <div className="offer-services-grid">
                {relevantServices.map((item, i) => (
                  <div key={i} className="offer-service-card">
                    <img src={item.image} alt={item.alt} className="offer-service-image" />
                    <div className="offer-service-info">
                      <div className="offer-service-badges">
                        {item.track === 'both' ? (
                          <>
                            <span className={trackClass('software')}>{t.services.trackSoftware}</span>
                            <span className={trackClass('hardware')}>{t.services.trackHardware}</span>
                          </>
                        ) : (
                          <span className={trackClass(item.track)}>{trackLabel(item.track)}</span>
                        )}
                      </div>
                      <h3 className="offer-service-title">{item.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default OfferPage;
